/**
 * Mixin to instantiate and control whiteboard view
 * provides an onWhiteboardViewClick method to toggle whiteboard view
 * and default controls for modal
 * expects consuming component to define `whiteboardViewData`
 * `whiteboardViewData:`should provide tab `tabKey`, an optional `highlightingModalSelector`,  and an `allowedHighlightingTabs` set
 * an `article` and a `splitTabData` obj should be also be defined
 * see split-tabs.js for other expected values
 */

const model = require('model/model');
const utilities = require('utilities');
const analyticsUtilities = require('utilities/analyticsUtilities');
/* fireEvent always called via analyticUtilities to allow stubbing in tests */
const {
	events: { WHITEBOARD_VIEW_ACTIVATE, WHITEBOARD_VIEW_DEACTIVATE, WHITEBOARD_TOOL_CLICK }
} = analyticsUtilities;
const splitTabsMixin = require('mixins/split-tabs');
const pubsub = require('events/pubsub');

const FONT_SIZE_CLASSES = ['', '-font-m', '-font-l', '-font-xl'];

module.exports = {
	mixins: [splitTabsMixin],
	modelData: {
		whiteboardView: 'whiteboardView',
		whiteboardHighlightDismiss: 'whiteboardHighlightDismiss',
		splitTab: 'splitTab'
	},
	data() {
		return {
			article: {},
			whiteboardViewData: {
				tabKey: '',
				highlightingModalSelector: '',
				allowedHighlightingTabs: new Set()
			},
			whiteboardDismissProduct: false,
			fontSizeClass: '',
			paragraphsNumbered: false,
			splitScreen: false,
			splitTabs: [],
			splitTabData: {
				tabKey: '',
				defaultTab: '',
				eventName: ''
			}
		};
	},
	computed: {
		whiteboardTab() {
			const { tabKey } = this.whiteboardViewData;

			return this[tabKey];
		}
	},
	beforeDestroy() {
		model.set('whiteboardView', false);
	},
	methods: {
		maybeOpenHighlightingModal() {
			const { highlightingModalSelector, allowedHighlightingTabs } = this.whiteboardViewData;
			if (!highlightingModalSelector || allowedHighlightingTabs.size === 0) {
				return;
			}

			const correctTab = allowedHighlightingTabs.has(this.whiteboardTab);

			if (this.whiteboardView && correctTab && !this.whiteboardHighlightDismiss && !this.whiteboardDismissProduct) {
				this.whiteboardDismissProduct = true;
				utilities.openModal(highlightingModalSelector);
			}
		},
		onCloseWhiteboardModalClick() {
			utilities.closeModals();
		},
		onWhiteboardHighlightDismiss() {
			this.whiteboardHighlightDismiss = true;
			utilities.closeModals();
		},
		onWhiteboardViewClick() {
			const whiteboardView = !this.whiteboardView;
			this.whiteboardView = whiteboardView;

			const product = this.article;
			const tab = this.whiteboardTab;
			let { uuid: pid, productType, supplemental_texts } = product || {};
			let parentPid = undefined;
			let challenge = undefined;
			let boost = undefined;
			let dteGroup = undefined;
			let eventName = WHITEBOARD_VIEW_DEACTIVATE;

			const contentSection = model.get('contentSection');

			if (tab === 'decodables') {
				parentPid = pid;
				pid = model.get('decodablesArticle');
				dteGroup = model.get('decodablesSection');
			} else if (contentSection) {
				parentPid = pid;
				pid = contentSection;
			}
			const origin = model.get('articleEntryPoint');

			const hasSupplementalText = productType === 'AAD' && supplemental_texts;

			if (hasSupplementalText) {
				const { challenge: challengeObj, support: boostObj } = supplemental_texts;

				if (challengeObj && challengeObj[pid]) {
					challenge = true;
				} else if (boostObj && boostObj[pid]) {
					boost = true;
				}
			}

			if (whiteboardView) {
				eventName = WHITEBOARD_VIEW_ACTIVATE;
				this.maybeOpenHighlightingModal();
			}

			analyticsUtilities.fireEvent(eventName, {
				pid,
				tab,
				parentPid,
				boost,
				challenge,
				dteGroup,
				origin
			});

			model.remove('splitTab');
			this.fontSizeClass = '';
			this.paragraphsNumbered = false;
			this.splitScreen = false;
			window.dispatchEvent(new UIEvent('resize'));
		},
		onParagraphNumbersClick() {
			const state = !this.paragraphsNumbered;
			this.paragraphsNumbered = state;

			analyticsUtilities.fireEvent(WHITEBOARD_TOOL_CLICK, {
				action_type: 'show_paragraph_numbers',
				state,
				pid: this.article.uuid
			});
		},
		onFontSizeClick() {
			const currentIndex = FONT_SIZE_CLASSES.indexOf(this.fontSizeClass);
			const index = (currentIndex + 1) % FONT_SIZE_CLASSES.length;
			this.fontSizeClass = FONT_SIZE_CLASSES[index];
			const state = ['n', 'm', 'l', 'xl'][index];

			analyticsUtilities.fireEvent(WHITEBOARD_TOOL_CLICK, {
				action_type: 'font_size',
				state,
				pid: this.article.uuid
			});

			pubsub.publish('font-size-changed');
		}
	}
};
